import { useIntl } from 'react-intl'

import { cn } from '@gto/ui/cn'
import type { ColumnDef } from '@gto/ui/data-table'
import { createColumns } from '@gto/ui/data-table'
import { CheckCircleIcon, XCircleIcon } from '@gto/ui/icons/solid'
import { Typography } from '@gto/ui/typography'

import { fluidColumn } from '@/components/table-columns'
import { useAuth } from '@/hooks/use-auth'
import { TableCellPlaceholder } from '@/pages/promotion/components/free-tournament-tickets/table-cell-placeholder'
import { RoomTitleWithLogo } from '@/pages/promotion/components/room-title-with-logo'
import { safeNumber } from '@/utils/helpers'

export type TicketStatusRow = {
  sites: string
  verified: boolean
  gto_wizard_subscription: string | null
  /** The API hands this back as either, so both are accepted and `safeNumber` narrows. */
  prize_earned: string | number
  id: string
  logo: string | null
  name: string
}

export const useTicketsStatusCols = (): ColumnDef<TicketStatusRow>[] => {
  const { formatMessage, formatNumber } = useIntl()
  const { isAuthorized } = useAuth()

  // Every column but the room itself is the signed-in user's OWN status, so it sits behind a
  // placeholder until they log in.
  return createColumns<TicketStatusRow>([
    {
      accessorKey: 'logo',
      header: formatMessage({ id: 'promo.tournamentTickets.table.room' }),
      ...fluidColumn({ min: 150, align: 'center', headerAlign: 'center' }),
      cell: ({ row }) => (
        <RoomTitleWithLogo
          logo={row.original.logo}
          name={row.original.name}
        />
      ),
    },
    {
      accessorKey: 'verified',
      header: formatMessage({ id: 'promo.tournamentTickets.table.accCreatedViaRbw' }),
      ...fluidColumn({ min: 200, align: 'center', headerAlign: 'center' }),
      cell: ({ row }) => {
        if (!isAuthorized) {
          return <TableCellPlaceholder />
        }

        const { verified } = row.original
        const Icon = verified ? CheckCircleIcon : XCircleIcon

        return (
          <div className="flex items-center justify-center gap-2.5">
            <Typography variant="body2">
              {formatMessage({ id: verified ? 'common.labels.yes' : 'common.labels.no' })}
            </Typography>
            <div
              className={cn(
                'flex items-center justify-center',
                verified ? 'text-primary-main' : 'text-error-main',
              )}
            >
              <Icon
                height={18}
                width={18}
              />
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: 'gto_wizard_subscription',
      header: formatMessage({ id: 'promo.tournamentTickets.table.gtoWizardSubscription' }),
      ...fluidColumn({ min: 150, align: 'center', headerAlign: 'center' }),
      cell: ({ row }) =>
        isAuthorized ? (
          <span className="capitalize">
            {row.original.gto_wizard_subscription ?? formatMessage({ id: 'common.labels.none' })}
          </span>
        ) : (
          <TableCellPlaceholder />
        ),
    },
    {
      accessorKey: 'prize_earned',
      header: formatMessage({ id: 'promo.tournamentTickets.table.ticketEarned' }),
      ...fluidColumn({ min: 150, align: 'center', headerAlign: 'center' }),
      cell: ({ row }) => {
        if (!isAuthorized) {
          return <TableCellPlaceholder />
        }

        const sum = safeNumber(row.original.prize_earned)

        return sum
          ? formatNumber(sum, { style: 'currency', currency: 'USD', minimumFractionDigits: 0 })
          : formatMessage({ id: 'common.labels.none' })
      },
    },
  ])
}
